import {
  Circle,
  Flex,
  Icon,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiPause, FiPlay } from 'react-icons/fi';

import { openViaProxy } from './openViaProxy';

type Props = {
  src: string;
  fileName: string;
};

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const CommentAudioPlayer: React.FC<Props> = ({ src, fileName }) => {
  const { t } = useTranslation();
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const togglePlay = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const handleSeek = React.useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  }, []);

  const handleDownload = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      await openViaProxy(src, fileName, 'download');
    } finally {
      setIsDownloading(false);
    }
  }, [src, fileName]);

  return (
    <Flex
      align="center"
      gap="3"
      p="2"
      borderWidth="thin"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      w="full"
      minW="260px"
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
      />
      <Circle
        as="button"
        size="9"
        bg="brand.500"
        color="white"
        flexShrink={0}
        onClick={togglePlay}
        aria-label={isPlaying ? t('common.pause') : t('common.play')}
      >
        <Icon as={isPlaying ? FiPause : FiPlay} fontSize="16px" />
      </Circle>
      <Flex flex="1" direction="column" minW="0" gap="1">
        <Slider
          value={currentTime}
          min={0}
          max={duration || 0}
          onChange={handleSeek}
          colorScheme="brand"
          size="sm"
          aria-label={t('common.audio_progress')}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
        <Text fontSize="xs" color="gray.500">
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>
      </Flex>
      <Circle
        as="button"
        size="7"
        bg="blue.500"
        flexShrink={0}
        cursor={isDownloading ? 'default' : 'pointer'}
        onClick={isDownloading ? undefined : handleDownload}
        aria-label={t('file_transfer.download_file')}
      >
        {isDownloading ? (
          <Spinner size="xs" color="white" thickness="2px" />
        ) : (
          <Icon as={FiDownload} color="white" fontSize="12px" />
        )}
      </Circle>
    </Flex>
  );
};
